import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { GridArticle } from './components';
import useArticlesPagination from './useArticlesPagination';
import { clsx } from 'clsx';
import {
  LoadingSpinner,
  useInfiniteScroll,
  useParameters,
} from '@web/utils/src';
import { ItemDetailsModal } from '@/components/ItemDetailsModal';
import { useCustomToast } from '@/hooks/useCustomToast';
import useLivePrices from '@/hooks/useLivePrices';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import { ErpType } from '@/types';
import { ArticleData, CartItem, CartItemData } from '@agrantis/cart';
import { addCartItem } from '@agrantis/cart/actions/cart';

interface InventoryCatalogProps {
  searchTerm?: string;
  categoryId?: string | null;
}

const InventoryCatalog = (props: InventoryCatalogProps) => {
  const { searchTerm, categoryId } = props;
  const [editLine, setEditLine] = useState<CartItem | null>();
  const [inventoryItem, setInventoryItem] = useState<ArticleData | null>();
  const [isItemDetailsModalOpen, setItemDetailsModalOpen] = useState(false);
  const toast = useCustomToast();
  const { retailerId } = useParameters<{ retailerId: string }>();
  const { t } = useTranslation();
  const dispatch = useAppDispatch();

  const user = useAppSelector((state) => state.user.data);
  const settings = useAppSelector((state) => state.retailer.retailer?.settings);
  // settings
  const isVolumeDiscountsEnabled = !settings?.hideVolumeDiscounts;
  const livePriceErpType = settings?.livePriceErpType;

  const { articles, fetching, error, loadMore, hasNextPage } =
    useArticlesPagination({
      categoryId: categoryId || null,
      searchTerm: searchTerm,
      erpCustomerGroupId: user?.erpCustomerGroupId || null,
      erpCustomerId: user?.erpCustomerId,
    });

  const inventories = useMemo(
    () =>
      livePriceErpType === ErpType.Serp
        ? articles.map((article) => ({
            inventoryId: article.item.id.toString(),
            erpArticleId: article.item?.retailerItemId!.toString(),
          }))
        : [],
    [articles, livePriceErpType],
  );

  const { livePrices, fetching: livePricesFetching } = useLivePrices({
    inventories,
    customerId: user?.erpCustomerId || undefined,
    retailerId: retailerId!,
    erpCustomerGroupID: user?.erpCustomerGroupId || undefined,
  });

  useInfiniteScroll(loadMore, fetching, !hasNextPage);

  const handleAddItemToCart = (data: {
    itemData: CartItemData;
    amount: number;
    price?: number;
  }) => {
    try {
      dispatch(
        addCartItem({
          ...data,
          cartKey: retailerId,
        }),
      );
      setEditLine(null);
      setInventoryItem(null);
      setItemDetailsModalOpen(false);
    } catch (error: any) {
      toast('error', error.message || t('global.something_went_wrong'));
      console.log(error);
    }
  };

  const handleOpenItemDetailsModal = (
    item: CartItem,
    articleData: ArticleData,
  ) => {
    setEditLine(item);
    setInventoryItem(articleData);
    setItemDetailsModalOpen(true);
  };

  if (error) return <p>Error: {error.message}</p>;

  return (
    <div className="py-2-0 mx-auto">
      {editLine && (
        <ItemDetailsModal
          addItemToCart={handleAddItemToCart}
          modalVisible={isItemDetailsModalOpen}
          setModalVisible={setItemDetailsModalOpen}
          cartItem={editLine}
          inventoryItem={inventoryItem}
        />
      )}
      <p
        className={clsx(
          searchTerm ? 'text-body1' : 'text-2-0  md:text-3-2',
          'font-bd mb-3-0 md:mb-2-0',
        )}
      >
        {searchTerm ? (
          <span>
            {`${t('search_bar.search_result')}: `}
            <span className="underline">{searchTerm}</span>
          </span>
        ) : (
          t('retailer.inventory_title')
        )}
      </p>
      {articles.length > 0 ? (
        <div
          className={clsx(
            'grid-cols-1 md:grid-cols-3',
            'grid gap-4-0 md:gap-2-0',
          )}
        >
          {articles.map((article) => (
            <GridArticle
              key={article.id}
              article={article}
              onItemOpen={(item: CartItem) =>
                handleOpenItemDetailsModal(item, article)
              }
              isVolumeDiscountsEnabled={isVolumeDiscountsEnabled}
              livePrices={livePrices}
              livePricesFetching={livePricesFetching}
            />
          ))}
        </div>
      ) : (
        <p className="text-body1 mt-4-0">
          {!fetching && t('retailer.inventory_no_match')}
        </p>
      )}
      {fetching && (
        <LoadingSpinner size="large" className="flex justify-center py-2-0" />
      )}
      <div id="infinite-scroll-trigger"></div>
    </div>
  );
};

export default InventoryCatalog;
